import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import CashbookServices from "../../../services/CashbookServices";
import LocationServices from "../../../services/LocationServices";
import UserServices from "../../../services/UserServices";

const readData = (response) => {
    const body = response?.data?.status
        ? response.data
        : response;

    return body?.data;
};

const toInitCap = (value = "") =>
    String(value)
        .trim()
        .toLowerCase()
        .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());

const getErrorMessage = (error, fallback) =>
    error?.response?.data?.message || error?.message || fallback;

const selectStyles = {
    menu: (base) => ({
        ...base,
        zIndex: 10
    })
};

const CashbookAddModal = ({
    show,
    handleClose,
    onCreated
}) => {
    const [stores, setStores] = useState([]);
    const [employees, setEmployees] = useState([]);

    const [storeId, setStoreId] = useState("");
    const [employeeId, setEmployeeId] = useState("");
    const [remark, setRemark] = useState("");

    const [storesLoading, setStoresLoading] = useState(false);
    const [employeesLoading, setEmployeesLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Reset the form and load stores when the modal opens.
    useEffect(() => {
        if (!show) return;

        let cancelled = false;

        setStoreId("");
        setEmployeeId("");
        setRemark("");
        setEmployees([]);
        setStores([]);
        setStoresLoading(true);

        const loadStores = async () => {
            try {
                const response = await LocationServices.getStore();
                const allStores = response?.data?.data;

                if (!Array.isArray(allStores)) {
                    throw new Error("Unexpected store API response.");
                }

                if (!cancelled) {
                    setStores(allStores);
                }
            } catch (error) {
                if (!cancelled) {
                    Swal.fire(
                        "Error",
                        getErrorMessage(error, "Unable to load stores."),
                        "error"
                    );
                }
            } finally {
                if (!cancelled) {
                    setStoresLoading(false);
                }
            }
        };

        loadStores();

        return () => {
            cancelled = true;
        };
    }, [show]);

    // Load employees assigned to the selected store.
    useEffect(() => {
        let cancelled = false;

        setEmployeeId("");
        setEmployees([]);

        if (!show || !storeId) {
            setEmployeesLoading(false);
            return;
        }

        setEmployeesLoading(true);

        const loadEmployees = async () => {
            try {
                const response =
                    await UserServices.getPassbookEmployeesByStore(storeId);

                const data = readData(response);

                if (!Array.isArray(data?.employees)) {
                    throw new Error("Unexpected employee API response.");
                }

                if (!cancelled) {
                    setEmployees(
                        data.employees.filter(
                            (employee) =>
                                String(employee.role || "")
                                    .trim()
                                    .toLowerCase() === "cashmaker"
                        )
                    );
                }
            } catch (error) {
                if (!cancelled) {
                    Swal.fire(
                        "Error",
                        getErrorMessage(error, "Unable to load employees."),
                        "error"
                    );
                }
            } finally {
                if (!cancelled) {
                    setEmployeesLoading(false);
                }
            }
        };

        loadEmployees();

        return () => {
            cancelled = true;
        };
    }, [show, storeId]);

    const submit = async (event) => {
        event.preventDefault();

        if (saving || storesLoading || employeesLoading) return;

        const selectedEmployee = employees.find(
            (employee) => String(employee._id) === employeeId
        );

        if (!storeId || !selectedEmployee) {
            await Swal.fire(
                "Required",
                "Please select a store and employee.",
                "warning"
            );
            return;
        }

        if (selectedEmployee.hasPassbook) {
            await Swal.fire(
                "Already Exists",
                "This employee already has a cashbook for this store.",
                "warning"
            );
            return;
        }

        setSaving(true);

        let createdPassbook;

        try {
            const response = await CashbookServices.createPassbook({
                storeId,
                employeeId,
                remark: remark.trim()
            });

            createdPassbook = readData(response)?.passbook;
        } catch (error) {
            setSaving(false);

            await Swal.fire(
                "Error",
                getErrorMessage(error, "Unable to create the cashbook."),
                "error"
            );

            return;
        }

        await Swal.fire({
            icon: "success",
            title: "Success",
            text: "Cashbook created successfully.",
            confirmButtonText: "OK",
            allowOutsideClick: false,
            allowEscapeKey: false
        });

        setSaving(false);
        handleClose();
        await onCreated?.(createdPassbook);
    };

    const storeOptions = stores.map((store) => ({
        value: String(store._id),
        label: [
            toInitCap(store.name),
            toInitCap(store.location)
        ]
            .filter(Boolean)
            .join(", "),
      isDisabled: false
    }));

    const employeeOptions = employees.map((employee) => ({
        value: String(employee._id),

        label:
            [toInitCap(employee.name), employee.mobileNo]
                .filter(Boolean)
                .join(" — ") +
            (employee.hasPassbook
                ? employee.passbookStatus === "inactive"
                    ? " (Inactive cashbook exists — reactivate)"
                    : " (Cashbook already exists for this store)"
                : ""),

        isDisabled: Boolean(employee.hasPassbook)
    }));

    const closeModal = () => {
        if (!saving) handleClose();
    };

    return (
        <Modal
            show={show}
            onHide={closeModal}
            size="lg"
            centered
            className="fade"
            backdrop="static"
            keyboard={!saving}
        >
            <div className="filter cm-content-box box-primary mt-1 mb-0">
                <Modal.Header closeButton={!saving}>
                    <Modal.Title>
                        <i className="fa fa-book me-2" aria-hidden="true" />
                        Add New Cashbook
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div className="cm-content-body form excerpt">
                        <div className="card-body">
                            <form onSubmit={submit}>
                                <div className="row">
                                    <div className="form-group mb-3 col-md-6">
                                        <label htmlFor="cashbook-store">
                                            Store / Location
                                        </label>

                                        <Select
                                            inputId="cashbook-store"
                                            options={storeOptions}
                                            value={
                                                storeOptions.find(
                                                    (option) => option.value === storeId
                                                ) || null
                                            }
                                            onChange={(selected) => {
                                                const nextStoreId = selected?.value || "";

                                                if (nextStoreId !== storeId) {
                                                    setEmployeeId("");
                                                    setEmployees([]);
                                                    setEmployeesLoading(Boolean(nextStoreId));
                                                    setStoreId(nextStoreId);
                                                }
                                            }}
                                            placeholder={
                                                storesLoading
                                                    ? "Loading Stores..."
                                                    : "Search Store..."
                                            }
                                            isSearchable
                                            isClearable
                                            isLoading={storesLoading}
                                            isDisabled={saving || storesLoading}
                                            noOptionsMessage={() => "No stores found"}
                                            className="custom-react-select"
                                            menuPlacement="auto"
                                            styles={selectStyles}
                                        />
                                    </div>

                                    <div className="form-group mb-3 col-md-6">
                                        <label htmlFor="cashbook-employee">
                                            Cash Maker
                                        </label>

                                        <Select
                                            inputId="cashbook-employee"
                                            options={employeeOptions}
                                            value={
                                                employeeOptions.find(
                                                    (option) => option.value === employeeId
                                                ) || null
                                            }
                                            onChange={(selected) =>
                                                setEmployeeId(selected?.value || "")
                                            }
                                            placeholder={
                                                !storeId
                                                    ? "Select Store First"
                                                    : employeesLoading
                                                        ? "Loading Cash Makers..."
                                                        : employeeOptions.length === 0
                                                            ? "No Cash Maker found for this store"
                                                            : "Search Cash Maker..."
                                            }
                                            isSearchable
                                            isClearable
                                            isLoading={employeesLoading}
                                            isDisabled={!storeId || employeesLoading || saving}
                                            noOptionsMessage={() =>
                                                employeeOptions.length === 0
                                                    ? "No Cash Maker found for this store"
                                                    : "No matching Cash Maker found"
                                            }
                                            className="custom-react-select"
                                            menuPlacement="auto"
                                            styles={selectStyles}
                                        />
                                    </div>

                                    <div className="form-group mb-3 col-md-12">
                                        <label htmlFor="cashbook-remark">
                                            Remark
                                        </label>

                                        <textarea
                                            id="cashbook-remark"
                                            className="form-control"
                                            rows={3}
                                            maxLength={1000}
                                            placeholder="Enter cashbook purpose or remark"
                                            value={remark}
                                            onChange={(event) =>
                                                setRemark(event.target.value)
                                            }
                                            disabled={saving}
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    className="btn btn-primary me-2"
                                    disabled={
                                        saving ||
                                        storesLoading ||
                                        employeesLoading ||
                                        !storeId ||
                                        !employeeId
                                    }
                                >
                                    {saving ? "Saving..." : "Submit"}
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    disabled={saving}
                                    onClick={closeModal}
                                >
                                    Cancel
                                </button>
                            </form>
                        </div>
                    </div>
                </Modal.Body>
            </div>
        </Modal>
    );
};

export default CashbookAddModal;